<template>
  <div class="research-group-list">
    <p class="help-text">
      <template v-if="hasGroupsRef">
        <template v-if="isSelfRef">Upload a file from your genealogy program to share a family tree with others.</template>
        <template v-else>These are the trees this user manages that are also visible to you.</template>
      </template>
      <template v-else>
        <template v-if="isSelfRef">You do not manage any trees, and no trees have been shared with you.</template>
        <template v-else>This user does not manage any trees that are visible to you.</template>
      </template>
    </p>
    <div v-if="!isOwnedGroupsUnderLimitRef && canUpgradeRef" class="alert alert-primary alert-sm">
      You have reached the limit on your plan and cannot add any more trees.
      <router-link to="/plans">
        Upgrade my plan
      </router-link>
    </div>
    <ul v-if="hasGroupsRef" class="list-unstyled group-list">
      <li v-for="rg in ownedResearchGroupsRef" :key="rg.id">
        <ResearchGroupCard :data-group-id="rg.id!"/>
      </li>
      <li v-for="rg in sharedResearchGroupsRef" :key="rg.id">
        <ResearchGroupCard :data-group-id="rg.id!"/>
      </li>
    </ul>
    <div v-if="canAddRef" class="actions-row">
      <router-link v-if="!hasGroupsRef" class="btn btn-primary" to="/syncapp">
        Upload my tree
      </router-link>
      <router-link v-else to="/learn/sync">
        Upload another tree
      </router-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.research-group-list {
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  h5 {
    margin-bottom: 1rem;
    font-size: 1rem;
    font-weight: 400;
  }

  .group-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;

  }

  &.show-all .group-list {
    margin-left: 1rem;
  }
  
  .actions-row {
    margin-top: 1.5rem;
    font-size: 0.875rem;
  }
}
</style>

<script setup lang="ts">
import { computed } from 'vue'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { DataGroup, DataGroupType, GroupMemberRole, ItemPermissions } from '@/gp/GroupAdminModel'
import { LoadMode } from '@/util/AsyncData'
import ResearchGroupCard from './ResearchGroupCard.vue'
import { useTokenManager } from '@/auth/TokenManager'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useUserStore } from '@/gp/UserStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { sortBy, uniq } from 'lodash'

const props = defineProps<{
  userId?: string
  forUserProfile?: boolean
}>()

const userStore = useUserStore()
const dataGroupStore = useDataGroupStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const familyGroupMemberStore = useFamilyGroupMemberStore()
const tokenManager = useTokenManager()

const userIdRef = computed(() => props.userId || tokenManager.current.userId)
const isSelfRef = computed(() => userIdRef.value == tokenManager.current.userId)
const userRef = computed(() => userStore.getAsyncUser(userIdRef.value, LoadMode.EnsureLoaded)?.data)
const userPermissions = computed(() => 
  userStore.getAsyncPermissions(userIdRef.value, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)

const visibleResearchGroupsRef = computed(() => 
  dataGroupStore.getVisibleGroups(LoadMode.EnsureLoaded).filter(dg => dg.groupType == DataGroupType.Research))

const ownedDataGroupIdsRef = computed(() => 
  dataGroupMemberStore.getMembersForUsers([userIdRef.value], LoadMode.EnsureLoaded)
    .filter(dm => dm.role == GroupMemberRole.Owner)
    .map(dm => dm.dataGroupId!)) 

const ownedResearchGroupsRef = computed(() =>
  dataGroupStore.getLoadedGroupList(ownedDataGroupIdsRef.value)
    .filter(dg => dg.groupType == DataGroupType.Research))

const familyGroupIdsRef = computed(() => 
  familyGroupMemberStore.getMembersForUsers([props.userId], LoadMode.EnsureLoaded)
  .map(fm => fm.familyGroupId!))

const sharedDataGroupIdsRef = computed(() => {
  const dms = dataGroupMemberStore.getMembersForFamilyGroups(familyGroupIdsRef.value, LoadMode.EnsureLoaded)
  return uniq(dms.map(dm => dm.dataGroupId!))
})
const sharedResearchGroupsRef = computed(() => {
  const researchGroups = dataGroupStore.getLoadedGroupList(sharedDataGroupIdsRef.value)
    .filter(dg => dg.groupType == DataGroupType.Research)
    .filter(dg => !ownedDataGroupIdsRef.value.includes(dg.id!))
  return sortBy(researchGroups, dg => dg.name)
})

const hasGroupsRef = computed(() => ownedResearchGroupsRef.value.length || sharedResearchGroupsRef.value.length)

const researchGroupLimitRef = computed(() => userRef.value?.limitsDisabled ? undefined : userRef.value?.limits?.researchGroupLimit)
const isOwnedGroupsUnderLimitRef = computed(() => isSelfRef.value && ownedResearchGroupsRef.value.length < (researchGroupLimitRef.value ?? Infinity))
const canUpgradeRef = computed(() => isSelfRef.value && !tokenManager.current.isSupervised)
const canAddRef = computed(() => !props.forUserProfile && isSelfRef.value && !tokenManager.current.isSupervised && isOwnedGroupsUnderLimitRef.value)

</script>
